import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Calendar,
  FileText,
  Edit,
  Trash2,
  Plus,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { Client } from "@/types";
import { ClientService } from "@/services/ClientService";
import { InvoiceCreditService } from "@/services/InvoiceCreditService";
import { CreditStatusBadge } from "./CreditStatusBadge";

interface ClientDetailsProps {
  client: Client;
  onEdit?: (client: Client) => void;
  onDelete?: (client: Client) => void;
  onClose?: () => void;
}

interface InvoiceSummary {
  totalInvoices: number;
  totalAmount: number;
  paidInvoices: number;
  unpaidInvoices: number;
  unpaidAmount: number;
  recentInvoices: any[];
}

interface CreditSummary {
  currentBalance: number;
  creditLimit: number;
  availableCredit: number;
  totalInvoiced: number;
  totalPaid: number;
  unpaidInvoices: number;
  recentTransactions: any[];
}

export const ClientDetails: React.FC<ClientDetailsProps> = ({
  client,
  onEdit,
  onDelete,
  onClose,
}) => {
  const [invoiceSummary, setInvoiceSummary] = useState<InvoiceSummary | null>(
    null
  );
  const [creditSummary, setCreditSummary] = useState<CreditSummary | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSummaries();
  }, [client.id]);

  const loadSummaries = async () => {
    try {
      setLoading(true);
      const [invoiceSummaryData, creditSummaryData] = await Promise.all([
        ClientService.getClientInvoiceSummary(client.id),
        InvoiceCreditService.getClientCreditSummary(client.id),
      ]);
      setInvoiceSummary(invoiceSummaryData);
      setCreditSummary(creditSummaryData);
    } catch (err) {
      console.error("Error loading summaries:", err);
      setError("Failed to load client information");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-TN", {
      style: "currency",
      currency: "TND",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const getStatusBadge = () => {
    if (!client.isActive) {
      return <Badge variant="secondary">Inactive</Badge>;
    }

    if (client.currentCreditBalance > 0) {
      return <Badge variant="outline">Has Credit</Badge>;
    }

    return <Badge variant="default">Active</Badge>;
  };

  const getCreditUtilization = () => {
    if (client.creditLimit === 0) return 0;
    return (client.currentCreditBalance / client.creditLimit) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2 rounded-full">
            <User className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{client.name}</h1>
            <div className="flex items-center space-x-2 mt-1">
              {getStatusBadge()}
              <CreditStatusBadge
                currentBalance={client.currentCreditBalance}
                creditLimit={client.creditLimit}
              />
              <span className="text-sm text-gray-500">
                Client since {formatDate(client.createdAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          {onEdit && (
            <Button variant="outline" onClick={() => onEdit(client)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="destructive" onClick={() => onDelete(client)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="credit">Credit Management</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {client.phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium">{client.phone}</p>
                    </div>
                  </div>
                )}

                {client.email && (
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{client.email}</p>
                    </div>
                  </div>
                )}
              </div>

              {client.address && (
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium">{client.address}</p>
                  </div>
                </div>
              )}

              {client.notes && (
                <div className="flex items-start space-x-3">
                  <FileText className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="font-medium">{client.notes}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Credit Information */}
          <Card>
            <CardHeader>
              <CardTitle>Credit Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <CreditCard className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Current Balance</p>
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(client.currentCreditBalance)}
                  </p>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Credit Limit</p>
                  <p className="text-xl font-bold text-gray-600">
                    {formatCurrency(client.creditLimit)}
                  </p>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <AlertCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Available Credit</p>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency(
                      client.creditLimit - client.currentCreditBalance
                    )}
                  </p>
                </div>
              </div>

              {client.creditLimit > 0 && (
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Credit Utilization</span>
                    <span>{getCreditUtilization().toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        getCreditUtilization() > 80
                          ? "bg-red-500"
                          : getCreditUtilization() > 60
                          ? "bg-yellow-500"
                          : "bg-green-500"
                      }`}
                      style={{
                        width: `${Math.min(getCreditUtilization(), 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoice Summary */}
          {loading ? (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              </CardContent>
            </Card>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : invoiceSummary ? (
            <Card>
              <CardHeader>
                <CardTitle>Invoice Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-500">Total Invoices</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {invoiceSummary.totalInvoices}
                    </p>
                  </div>

                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(invoiceSummary.totalAmount)}
                    </p>
                  </div>

                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-gray-500">Paid Invoices</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {invoiceSummary.paidInvoices}
                    </p>
                  </div>

                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-gray-500">Unpaid Amount</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(invoiceSummary.unpaidAmount)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <CardTitle>Invoice History</CardTitle>
            </CardHeader>
            <CardContent>
              {invoiceSummary?.recentInvoices.length ? (
                <div className="space-y-3">
                  {invoiceSummary.recentInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div>
                        <p className="font-medium">
                          Invoice #{invoice.id.slice(0, 8)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(new Date(invoice.created_at))}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">
                          {formatCurrency(invoice.total_amount)}
                        </p>
                        <Badge
                          variant={
                            invoice.payment_status === "paid"
                              ? "default"
                              : invoice.payment_status === "partial"
                              ? "secondary"
                              : "destructive"
                          }
                        >
                          {invoice.payment_status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No invoices found for this client.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credit">
          <Card>
            <CardHeader>
              <CardTitle>Credit Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Credit management features will be available in a future
                    update. This will include credit transaction history,
                    payment plans, and balance adjustments.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
